/**
 * Tasks API Client - HTTP client for task endpoints
 */

import type { TaskStatus, PullRequestStatus, NoPrCategory, NoPrReviewStatus } from '@resolve-ai/shared';
import { fetchWithAuth } from './api-client';

/**
 * Raw SDK message from agent execution (JSONB from task_message table)
 * Loosely typed since the shape comes from @anthropic-ai/claude-agent-sdk
 */
export interface RawAgentMessage {
  type: 'system' | 'assistant' | 'user' | 'result' | string;
  uuid?: string;
  session_id?: string;
  parent_tool_use_id?: string | null;
  message?: {
    id?: string;
    role?: string;
    type?: string;
    content?: Array<{
      type: string;
      text?: string;
      id?: string;
      name?: string;
      input?: Record<string, unknown>;
      tool_use_id?: string;
      content?: unknown[];
    }>;
  };
  subtype?: string;
  result?: string;
  [key: string]: unknown;
}

/**
 * Task type
 */
export interface Task {
  id: string;
  projectId: string;
  ticketKey: string;
  ticketUrl: string | null;
  ticketTitle: string | null;
  status: TaskStatus;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  noPrCategory: NoPrCategory | null;
  noPrReason: string | null;
  noPrReviewStatus: NoPrReviewStatus | null;
  noPrReviewedBy: string | null;
  noPrReviewedAt: string | null;
  resumedFromTaskId: string | null;
  project: {
    id: string;
    name: string;
    tenantId: string;
  };
}

/**
 * Human verdict on a no-PR decision
 */
export type NoPrVerdict = Exclude<NoPrReviewStatus, 'pending'>;

/**
 * Review fields returned after recording a no-PR verdict
 */
export interface NoPrReviewResult {
  id: string;
  noPrReviewStatus: NoPrReviewStatus;
  noPrReviewedBy: string | null;
  noPrReviewedAt: string | null;
}

/**
 * Pull Request type
 */
export interface PullRequest {
  id: string;
  taskId: string;
  prNumber: number;
  prUrl: string;
  prTitle: string | null;
  branchName: string | null;
  status: PullRequestStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * AI usage totals for one phase/category + model pair
 */
export interface UsageCategoryBreakdown {
  category: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  costCents: number;
}

/**
 * AI usage summary for a task (admin only)
 */
export interface TaskUsageSummary {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostCents: number;
  byCategory: UsageCategoryBreakdown[];
}

/**
 * A fallback patch uploaded when github_push/github_create_pr failed after
 * the fix was already committed and reviewed locally
 */
export interface TaskPatch {
  id: string;
  repoFullName: string;
  branchName: string;
  createdAt: string;
}

/**
 * Tasks API client
 */
export const tasksApi = {
  /**
   * Get task by ID
   */
  async getTask(id: string): Promise<{ task: Task }> {
    return fetchWithAuth<{ task: Task }>(`/tasks/${id}`);
  },

  /**
   * Get pull requests for a task
   */
  async getPullRequests(id: string): Promise<{ pullRequests: PullRequest[] }> {
    return fetchWithAuth<{ pullRequests: PullRequest[] }>(`/tasks/${id}/pull-requests`);
  },

  /**
   * Get agent messages for a task
   */
  async getMessages(id: string): Promise<{ messages: RawAgentMessage[] | null }> {
    return fetchWithAuth<{ messages: RawAgentMessage[] | null }>(`/tasks/${id}/messages`);
  },

  /**
   * Get AI usage summary for a task (admin only)
   */
  async getUsage(id: string): Promise<{ usage: TaskUsageSummary }> {
    return fetchWithAuth<{ usage: TaskUsageSummary }>(`/tasks/${id}/usage`);
  },

  /**
   * Get fallback patches recorded for a task (uploaded when publish failed)
   */
  async getPatches(id: string): Promise<{ patches: TaskPatch[] }> {
    return fetchWithAuth<{ patches: TaskPatch[] }>(`/tasks/${id}/patches`);
  },

  /**
   * Mint a fresh presigned download URL for a patch
   */
  async getPatchDownloadUrl(id: string, patchId: string): Promise<{ url: string }> {
    return fetchWithAuth<{ url: string }>(`/tasks/${id}/patches/${patchId}/download`);
  },

  /**
   * Cancel a pending task before it launches (admin only)
   */
  async cancelTask(id: string): Promise<{ message: string }> {
    return fetchWithAuth<{ message: string }>(`/tasks/${id}/cancel`, {
      method: 'POST',
    });
  },

  /**
   * Approve or reject the agent's decision to finish without a PR.
   * Can be called again to change the verdict.
   */
  async reviewNoPrDecision(id: string, verdict: NoPrVerdict): Promise<{ task: NoPrReviewResult }> {
    return fetchWithAuth<{ task: NoPrReviewResult }>(`/tasks/${id}/no-pr-review`, {
      method: 'POST',
      body: JSON.stringify({ verdict }),
    });
  },
};
